<template lang="pug">
.sticker-collection-on-detail(v-if="shouldRender")
  .collection-pill-row(v-if="showCollectionPill")
    span.collection-pill Collection

  .collection-header
    .collection-heading-row
      h3.collection-heading The Collection
      span.collection-progress(
        v-if="collection.showProgress && collection.totalStickers > 0"
      ) {{ progressLabel }}
    p.collection-subtitle {{ goalText }}
    router-link.collection-name-row(:to="collectionPagePath")
      span.collection-name {{ collection.title }}
      span.view-collection-hint View collection →

  .collection-scroll-wrapper
    .collection-scroll-container
      sticker-collection-grid-item(
        v-for="entry in sortedItems"
        :key="entry.id"
        :item="entry"
        :is-current="entry.sticker.id === currentStickerId"
        :inline-tooltip="false"
        @select="onSelectSticker"
        @hint="activeHint = $event"
      )
  p.collection-hint(:class="{ visible: Boolean(activeHint) }") {{ activeHint || '\u00a0' }}
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import StickerCollectionGridItem from './StickerCollectionGridItem.vue';
import {
  completionGoalText,
  completionProgressLabel,
} from '@/utils/stickerCollectionProgress';
import type {
  StickerCollectionForSticker,
  StickerCollectionStickerRef,
} from '@/data/stickerCollection.types';

export default defineComponent({
  name: 'StickerCollectionOnDetail',
  components: { StickerCollectionGridItem },
  props: {
    collection: {
      type: Object as () => StickerCollectionForSticker,
      required: true,
    },
    currentStickerId: {
      type: String,
      required: true,
    },
    showCollectionPill: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['select-sticker'],
  setup(props, { emit }) {
    const activeHint = ref<string | null>(null);

    const sortedItems = computed(() => [...(props.collection.items ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder));

    const shouldRender = computed(() => sortedItems.value.length > 0);

    // Against stickersRequiredToComplete, which can be below the total.
    const progressLabel = computed(() => completionProgressLabel(props.collection));
    const goalText = computed(() => completionGoalText(props.collection));

    const collectionPagePath = computed(
      () => `/web/sticker-collection/${props.collection.slug || props.collection.id}`,
    );

    function onSelectSticker(sticker: StickerCollectionStickerRef) {
      emit('select-sticker', sticker);
    }

    return {
      activeHint,
      sortedItems,
      shouldRender,
      progressLabel,
      goalText,
      collectionPagePath,
      onSelectSticker,
    };
  },
});
</script>

<style lang="scss" scoped>
.sticker-collection-on-detail {
  margin-bottom: 1.25rem;
}

.collection-pill-row {
  margin-bottom: 0.75rem;
}

.collection-pill {
  display: inline-block;
  background: #0d9488;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  line-height: 1.33;
}

.collection-header {
  margin-bottom: 0.75rem;
}

.collection-heading-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.125rem;
}

.collection-heading {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1a2744;
  margin: 0;
}

.collection-progress {
  font-size: 0.75rem;
  font-weight: 700;
  color: #0d9488;
  flex-shrink: 0;
}

.collection-subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 0.75rem;
}

.collection-name-row {
  text-decoration: none;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.375rem 0.75rem;
}

.collection-name {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #374151;
}

.collection-name-row:hover {
  .collection-name {
    color: #0d9488;
  }

  .view-collection-hint {
    color: #0d9488;
  }
}

.view-collection-hint {
  font-size: 0.6875rem;
  font-weight: 600;
  color: #9ca3af;
}

.collection-scroll-wrapper {
  // Room for the current-sticker ring and tile hover scale, which overflow-x would clip.
  margin: -0.375rem -0.375rem 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.collection-hint {
  margin: 0.375rem 0 0;
  min-height: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  opacity: 0;
  transition: opacity 0.15s ease;

  &.visible {
    opacity: 1;
  }
}

.collection-scroll-container {
  display: flex;
  gap: 0.625rem;
  padding: 0.375rem 0.375rem 0.25rem;
}
</style>
